import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaInjector from '@/components/base/SchemaInjector';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { websiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/mocks/schema';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'xinwen');
  useScrollToTop();

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const webPageSchema = getWebPageSchema(
    `https://www.chuanmeizaixian.com/category/${activeCategory}`,
    `${currentCategory?.name || ''} - 传媒在线观看`,
    currentCategory?.description || '',
  );
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: 'https://www.chuanmeizaixian.com/' },
    { name: currentCategory?.name || '', url: `https://www.chuanmeizaixian.com/category/${activeCategory}` },
  ]);

  return (
    <>
      <SchemaInjector schema={[websiteSchema, webPageSchema, breadcrumbSchema]} />

      <main>
        <Navbar />

        <div className="pt-20 bg-background-100">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: currentCategory?.name || '' },
            ]}
          />
        </div>

        <section className="w-full px-4 md:px-8 py-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory?.name}
              </h1>
              <p className="text-foreground-600 text-base">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-300 cursor-pointer"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <div className="flex items-center gap-3">
                          <span>{article.date}</span>
                          <span>{article.author}</span>
                        </div>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-base">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}